import { Button, Spin, Table, Tabs, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Chart, registerables } from 'chart.js'
import moment from 'moment'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Bar, Line } from 'react-chartjs-2'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { useApi } from '../../api/useApi'
import { setUtförareCases } from '../../api/utförareApi'
import ViewHeader from '../../components/viewHeader/ViewHeader'
import FilterToolbar from '../../components/filterToolbar/FilterToolbar'
import { CreateFilterConfig, DataType } from '../../components/tablesConfig/filterconfig'
import { LÄGE_FILTER } from '../../components/tablesConfig/hanteringColumn'
import { IBrukareFlag, IMedarbetareFlag, selectUtförareByName } from '../../store/utförareSlice'
import { BLUE, GREEN, getColorByIndex } from '../benchmarking/util/colors'
import { BORDER_WIDTH } from '../benchmarking/util/constants'
import { förklaringsfärg, färgrutaIHöjdMedTexten, grafTooltip, perBrukare } from './grafTooltip'
import { splitParams } from '../../utils/filterConfig'
import { setCaseAction } from '../arende/store/caseReducer/actions'
import HandleFlagModal from '../arende/components/handleFlag/HandleFlagModal'
import { setTableFilterAction } from '../../components/tablesConfig/store/actions'
import { selectTableFilter } from '../../components/tablesConfig/store/selectors'
import '../../styles/flagLink.css'
import { getUtförareTab, setUtförareTab } from '../arendeFordelning/store/tabSlice'

export interface IRiskFlag {
  number: number
  name: string
  avg: string
  level_avg: number
}

export interface IMultiDataSet {
  label: string
  data: number[]
  borderColor: string
  backgroundColor: string
}

export interface IMultiChart {
  labels: string[]
  datasets: IMultiDataSet[]
}

export interface IUtförareTrendPoint {
  month: string // 'YYYY-MM'
  brukareCount: number
  kommunBrukareCount: number
  // Summerad risknivå per flaggtyp (nyckel = flaggtyp-nummer). Filtreras klientsidan.
  utforareByType: { [type: string]: number }
  kommunByType: { [type: string]: number }
}

// Idempotent — gör Trend-fliken självförsörjande oavsett om Utförare-översikten laddats först.
Chart.register(...registerables)

const api_url = process.env.REACT_APP_API_URL
const TREND_DEFAULT_MONTHS = 12
// Samma typsnitt/storlek som stapelgraferna i Utförare-vyn.
const CHART_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"
const CHART_TICK_COLOR = 'rgba(0, 0, 0, 0.85)'
// Graferna i Flagghistorik: viewport-relativ höjd, så de fyller skärmen utan att skala med
// datamängden. Två grafer över varandra delar på höjden. Offseten rymmer header, verktygsrad, flikar och
// två rubriker. Under minsta höjden blir graferna oläsbara, och då får sidan hellre skrolla.
const TREND_GRAF_HOJD = 'calc((100vh - 400px) * 0.6)'
const TREND_GRAF_MINHOJD = 336

// Båda graferna får samma y-axelbredd. Chart.js räknar annars ut bredden ur siffrorna, så
// "0,3" och "1,25" ger olika bredd – och då står graferna inte rakt under varandra.
// 80 px rymmer rubriken och en siffra som "0,005" i 14 px.
const TREND_Y_AXEL_BREDD = 80
const trendYAxelBredd = (scale: { width: number }) => {
  scale.width = TREND_Y_AXEL_BREDD
}

/**
 * Förklaringen till höger om graferna i Flagghistorik.
 *
 * FAST BREDD, inte anpassad efter texten. Båda graferna har en sådan kolumn, och två lika breda
 * kolumner ger två lika breda grafer – tillsammans med y-axlarnas fasta bredd står x-axlarna då
 * rakt under varandra. En bredd uträknad ur texten hade gett graf 1 (korta namn) en annan bredd
 * än graf 2 (flaggnamn), och då var axlarna förskjutna igen.
 *
 * 230 px rymmer det längsta flaggnamnet i repot, "Ordinarie arbetstid och övertid", som tar 210 px
 * i 14px. Ett längre namn kortas med ellips och står i sin helhet när man pekar på det.
 *
 * Kolumnen bredvid grafen, inte ovanpå den: tidigare låg förklaringen i grafens hörn och krävde
 * reserverad plats inuti Chart.js, vilket vilade på att två tal alltid var lika.
 *
 * Bara visning. Chart.js brukar låta ett klick i förklaringen dölja en serie, men Flagga-filtret
 * gör redan det, delat och sparat mellan flikarna. Två sätt att dölja samma sak skulle börja
 * säga emot varandra.
 */
const TREND_FORKLARING_BREDD = 230

type TrendForklaringPost = { text: string; fyllning: string; kant: string }

const TrendForklaring = ({ poster }: { poster: TrendForklaringPost[] }) => (
  <div
    style={{
      width: TREND_FORKLARING_BREDD,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      // Samma luft upptill som graferna har i sin layout, så att första raden står i höjd med
      // plottytans överkant. Rullar hellre än att växa ut över grafen nedanför.
      padding: '8px 0 0 12px',
      overflowY: 'auto',
    }}
  >
    {poster.map((p) => (
      <div key={p.text} title={p.text} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <div style={{ width: 14, height: 14, backgroundColor: p.fyllning, border: `1.5px solid ${p.kant}`, borderRadius: 2, flexShrink: 0 }} />
        <span style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.text}</span>
      </div>
    ))}
  </div>
)

const ABSOLUTE_MIN_CHART_HEIGHT = 300
const BOTTOM_VIEWPORT_MARGIN = 24
const PX_PER_BAR = 28

function generateOptions() {
  return {
    indexAxis: 'y' as const,
    maintainAspectRatio: false,
    // Hela flaggans rad, inte bara stapeln under pekaren: tooltipen visar både utföraren och
    // kommunsnittet, som jämförelsegrafen i Flagghistorik.
    interaction: { mode: 'index' as const, axis: 'y' as const, intersect: false },
    elements: {
      bar: {
        borderWidth: 1.0,
      },
    },
    layout: {
      padding: { top: 0, right: 210 },
    },
    scales: {
      x: {
        border: { width: 1 },
        grid: { display: false },
        // Siffrorna i samma format som flaggnamnen, inte Chart.js standard (12 px, grått).
        ticks: { color: CHART_TICK_COLOR, font: { family: CHART_FONT_FAMILY, size: 14, weight: 'normal' as const } },
      },
      y: {
        beginAtZero: true,
        ticks: {
          display: true,
          color: 'rgba(0, 0, 0, 0.85)',
          font: {
            family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
            size: 14,
            weight: 'normal' as const,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
        onClick: () => null,
        labels: {
          color: 'rgb(255, 99, 132)',
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        ...grafTooltip(true),
        callbacks: {
          labelColor: förklaringsfärg,
          label: (item: any) => `${item.dataset?.label ?? ''}: ${perBrukare(item.parsed.x)}`,
        },
      },
    },
  }
}

function filterFromParam(flaglevel: string, deviation: string, data: any, type: string) {
  var res: any = []
  var filterData
  if (type === 'medarbetare') {
    filterData = data.medarbetare
  }
  if (type === 'brukare') {
    filterData = data.brukare
  }
  if (flaglevel !== 'null' && deviation !== 'null') {
    filterData.forEach((item: any) => {
      if (item.flaggnamn === deviation && Number(item.nivå) === Number(flaglevel)) {
        res.push(item)
      }
    })
  } else if (flaglevel !== 'null') {
    filterData.forEach((item: any) => {
      if (Number(item.nivå) === Number(flaglevel)) {
        res.push(item)
      }
    })
  } else if (deviation !== 'null') {
    filterData.forEach((item: any) => {
      if (item.flaggnamn === deviation) {
        res.push(item)
      }
    })
  } else {
    return filterData
  }
  return res
}

const SingleUtforare = () => {
  const navigate = useHistory()
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true)
  const [data, setData] = useState<IMultiChart>({
    labels: [],
    datasets: [],
  })
  // Vyn visar alltid det riskvägda värdet (flaggornas risknivå tas alltid med).
  const checked = true
  const [avgData, setAvgData] = useState<IRiskFlag[] | null>(null)
  // Trend-fliken: historik per körningsmånad, väljbart intervall + flaggtyp-filter.
  const [trendData, setTrendData] = useState<IUtförareTrendPoint[] | null>(null)
  // Flaggtypernas namn för hela intervallet, ur svaret. Se flaggtypsvalet nedan för varför.
  const [trendFlaggnamn, setTrendFlaggnamn] = useState<{ [type: string]: string }>({})
  const [trendLoading, setTrendLoading] = useState<boolean>(false)
  const [filteredData, setFilteredData] = useState([])
  const [filteredBrukareData, setFilteredBrukareData] = useState([])
  const dispatch = useDispatch()
  const api = useApi()
  const chartWrapperRef = useRef<HTMLDivElement>(null)
  const [viewportMinHeight, setViewportMinHeight] = useState(ABSOLUTE_MIN_CHART_HEIGHT)

  type Param = {
    utforare: string
    from: string
    to: string
    flaglevel: string
    deviation: string
  }

  const { utforare, from, to, deviation } = useParams<Param>()

  const utförareState = useSelector((state) => selectUtförareByName(state, utforare))[0]
  const currentTab = useSelector(getUtförareTab)
  const filterState = useSelector(selectTableFilter)

  // Skapad-fönster: persisteras per utförare i tableFilterReducer (#41) så det överlever navigering.
  // Reconciliation: PUSH (klick från översikt/lista) → ärv URL:ens from/to; POP (tillbaka) → behåll sparat.
  const datumKey = `datum::${utforare}`
  const savedWindow = filterState?.[datumKey]?.filter as { from?: string; to?: string } | undefined
  const [startDate, setStartDate] = useState(() =>
    navigate.action === 'POP' && savedWindow?.from ? savedWindow.from : from && from !== 'null' ? from : moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD')
  )
  const [endDate, setEndDate] = useState(() =>
    navigate.action === 'POP' && savedWindow?.to ? savedWindow.to : to && to !== 'null' ? to : moment().endOf('month').format('YYYY-MM-DD')
  )
  const setWindow = (f: string, t: string) => setTableFilterAction(datumKey, { from: f, to: t }, undefined, dispatch)

  // Trendens eget intervall, TREND_DEFAULT_MONTHS bakåt från sidans slutdatum. Skilt från
  // översiktens fönster med flit: historiken ska gå att se längre bak än den valda perioden.
  // Räknas från `endDate`, inte URL:ens `to` – den kan vara strängen 'null', och moment('null')
  // ger ett ogiltigt datum.
  const [trendFrom, setTrendFrom] = useState<string>(() => moment(endDate).subtract(TREND_DEFAULT_MONTHS - 1, 'months').startOf('month').format('YYYY-MM-DD'))
  const [trendTo, setTrendTo] = useState<string>(() => moment(endDate).endOf('month').format('YYYY-MM-DD'))

  // Delat avvikelse-filter + graf-sortläge, persisterat per utförare i tableFilterReducer (#16).
  // Nyckel `graf::<utförare>` (delat över flikarna): filter.deviation = avvikelse, sorter = sortläge.
  // Bevaras vid navigering (Redux lever). Reconciliation (PUSH=ärv URL / POP=behåll) i mount-effekten.
  const grafKey = `graf::${utforare}`
  const grafState = filterState?.[grafKey]
  const graphDeviation: string[] = grafState?.filter?.deviation ?? []
  const graphSortBy: 'alpha' | 'value' = grafState?.sorter ?? 'alpha'
  const setGraphDeviation = (values: string[]) => setTableFilterAction(grafKey, { deviation: values }, graphSortBy, dispatch)
  const setGraphSortBy = (v: 'alpha' | 'value') => setTableFilterAction(grafKey, { deviation: graphDeviation }, v, dispatch)

  // Utförarens data: återanvänd förhandshämtad (ärvt fönster) om den finns, annars hämta eget per fönster.
  const [utf, setUtf] = useState<any>(() => utförareState ?? null)

  /**
   * Löpnummer för sidans hämtningar – bara svaret på den senaste frågan används (#101).
   *
   * Byts perioden medan en hämtning pågår är två frågor ute samtidigt, och svaren kan komma i
   * omvänd ordning. Utan numret visades det svar som kom sist, som då kunde gälla en annan period
   * än den som står i periodväljaren. Ett svar vars nummer inte längre är det senaste kastas.
   */
  const utförareFråga = useRef(0)
  const snittFråga = useRef(0)
  const trendFråga = useRef(0)

  const loadUtförare = useCallback(
    async (f: string, t: string) => {
      const nr = ++utförareFråga.current
      const all = await setUtförareCases(api, f, t)
      if (nr !== utförareFråga.current) return
      setUtf((all || []).find((u: any) => u.namn === utforare) ?? null)
    },
    [api, utforare]
  )

  /**
   * Flaggtyper utföraren haft under trendens intervall, sorterade på nummer.
   *
   * Byggs ur trenddatan och inte ur utförarens flaggor i översiktens fönster. En flaggtyp som
   * bara finns längre bak – till exempel en som gått ned till noll – saknas i det aktuella
   * fönstret, och gick då inte att välja i filtret eller namnge i historiken. Det är just den
   * sortens flaggtyp flikens staplar är till för att visa.
   *
   * Ordningen är fast och delas med staplarna, så att varje flaggtyp behåller sin färg när
   * filtret ändras.
   */
  const trendTyper = useMemo(() => {
    const typer = new Set<string>()
    ;(trendData ?? []).forEach((p) => Object.keys(p.utforareByType).forEach((t) => typer.add(t)))
    return Array.from(typer).sort((a, b) => Number(a) - Number(b))
  }, [trendData])

  /**
   * Flaggtyperna som det delade flaggfiltret pekar ut, som typnummer – eller null när inget
   * filter är valt.
   *
   * Filtret bär flaggNAMN (samma som på övriga flikar), medan trenddatan är nycklad på typnummer.
   * Översättningen går genom hela namnlistan från servern, inte bara utförarens typer, så att
   * kommunsnittet kan filtreras på en flaggtyp som utföraren själv saknar.
   *
   * null och tom lista är olika saker: null betyder alla flaggtyper, en tom lista betyder att
   * filtret pekar på något som inte förekommer i intervallet – och då ska graferna visa noll.
   */
  const trendValdaTyper = useMemo<string[] | null>(
    () => (graphDeviation.length > 0 ? Object.keys(trendFlaggnamn).filter((t) => graphDeviation.includes(trendFlaggnamn[t])) : null),
    [graphDeviation, trendFlaggnamn]
  )

  // Datahämtning + fönster-reconciliation vid mount (#41):
  // POP med sparat fönster → behåll det och hämta dess data (förhandshämtningen matchar URL, ej sparat).
  // Annars: PUSH → seeda sparat fönster med URL-arvet; kall ingång (ej förhandshämtat) → hämta.
  useEffect(() => {
    if (navigate.action === 'POP' && savedWindow?.from && savedWindow?.to) {
      loadUtförare(savedWindow.from, savedWindow.to)
    } else {
      if (navigate.action !== 'POP') setWindow(startDate, endDate)
      if (!utförareState) loadUtförare(startDate, endDate)
    }
  }, [])

  // Håll tabellernas data i synk med hämtad utförare (fix: sattes tidigare bara en gång).
  useEffect(() => {
    if (utf) {
      setFilteredData(utf.medarbetare)
      setFilteredBrukareData(utf.brukare)
    }
  }, [utf])

  function handleDatesChange(range: any) {
    if (!range || !range[0] || !range[1]) return
    const f = range[0].startOf('month').format().split('T')[0]
    const t = range[1].endOf('month').format().split('T')[0]
    setStartDate(f)
    setEndDate(t)
    setWindow(f, t)
    loadUtförare(f, t)
  }

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<DataType[]>([])
  const [handleOpen, setHandleOpen] = useState<boolean>(false)
  const rowSelection = {
    fixed: 'right' as const,
    selectedRowKeys,
    onChange: (keys: React.Key[], rader: DataType[]) => {
      setSelectedRowKeys(keys)
      setSelectedRows(rader)
      setBtnDisabled(keys.length === 0)
      setCaseAction('SET_CASE', rader, null, dispatch)
    },
  }

  // Båda flikarna (personal och brukare) delar radval och knapp, så en hantering hämtar
  // om hela utföraren — det är ändå ett anrop.
  const efterHantering = () => {
    setHandleOpen(false)
    setSelectedRowKeys([])
    setSelectedRows([])
    setBtnDisabled(true)
    setCaseAction('SET_CASE', [], null, dispatch)
    loadUtförare(startDate, endDate)
  }

  const SELECTION_COLUMN_WIDTH = 32
  const sumColumnWidths = (cols: ColumnsType<DataType> | undefined) => (cols || []).reduce((sum, col) => sum + ((col.width as number) || 0), 0) + SELECTION_COLUMN_WIDTH

  // Nyckla filter/sort-state per utförare (inte bara per flik), annars "läcker" ett
  // filter (t.ex. personnummer) över till nästa utförare och döljer allt där.
  const medarbetareKey = `medarbetare::${utforare}`
  const brukareKey = `brukare::${utforare}`

  // Avvikelse-alternativ = unionen av utförarens flaggnamn. Avvikelse är DELAT filter (graphDeviation)
  // → styr graf + båda tabellerna.
  const deviationOptions = utf
    ? Array.from(new Set([...(utf.brukare || []).map((f: any) => f.flaggnamn as string), ...(utf.medarbetare || []).map((f: any) => f.flaggnamn as string)]))
        .sort()
        .map((n) => ({ value: n, label: n }))
    : []

  // Per-flik-filter (namn/personnummer/risknivå) ligger i tableFilterReducer per stateKey — bevaras
  // per utförare (som sorteringen) och läcker inte mellan utförare. setTableFilterAction skriver hela
  // {filter, sorter}, så vi mergar: popovern rör bara filter, tabellens onChange bara sorter.
  const medFilter = filterState?.[medarbetareKey]?.filter ?? {}
  const bruFilter = filterState?.[brukareKey]?.filter ?? {}
  const setFilterPart = (stateKey: string, key: string, values: (string | number)[]) => {
    const cur = filterState?.[stateKey] ?? {}
    setTableFilterAction(stateKey, { ...(cur.filter ?? {}), [key]: values }, cur.sorter, dispatch)
  }
  const setSorterFor = (stateKey: string, sorter: any) => {
    const cur = filterState?.[stateKey] ?? {}
    setTableFilterAction(stateKey, cur.filter ?? {}, sorter, dispatch)
  }
  const clearFilter = (stateKey: string) => {
    const cur = filterState?.[stateKey] ?? {}
    setTableFilterAction(stateKey, {}, cur.sorter, dispatch)
    setGraphDeviation([])
  }

  const uniqStr = (vals: any[]) =>
    Array.from(new Set(vals.filter((v): v is string => !!v)))
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }))
  const riskOptions = (rows: any[]) =>
    Array.from(new Set(rows.map((r) => r.nivå)))
      .sort((a, b) => (a as number) - (b as number))
      .map((v) => ({ value: v as number, label: v as number }))
  const avvikelseSelect = { key: 'deviation', label: 'Flagga', options: deviationOptions, value: graphDeviation, onChange: (v: any) => setGraphDeviation(v) }
  // Flagghistorikens filter ÄR det delade filtret – samma värde, samma sparande per utförare,
  // samma etikett. Bara alternativen är fler: dagens fönster saknar flaggtyper som bara finns
  // längre bak i historiken, och de ska gå att välja här.
  const trendAvvikelseSelect = {
    ...avvikelseSelect,
    options: Array.from(new Set([...deviationOptions.map((o: { value: string }) => o.value), ...trendTyper.map((t) => trendFlaggnamn[t]).filter(Boolean)]))
      .sort()
      .map((n) => ({ value: n, label: n })),
  }
  // Fast alternativlista, som på övriga flaggytor — härledd ur datan skulle "Pågående"
  // försvinna ur filtret precis när man vill kunna välja det.
  const statusSelect = (stateKey: string, aktiva: string[]) => ({
    key: 'hantering',
    label: 'Status',
    options: LÄGE_FILTER,
    value: aktiva,
    onChange: (v: any) => setFilterPart(stateKey, 'hantering', v),
  })
  const medarbetareSelects = [
    { key: 'name', label: 'Namn', options: uniqStr((filteredData as any[]).map((r) => r.namn)), value: medFilter.name ?? [], onChange: (v: any) => setFilterPart(medarbetareKey, 'name', v) },
    avvikelseSelect,
    { key: 'risk', label: 'Risknivå', options: riskOptions(filteredData as any[]), value: medFilter.risk ?? [], onChange: (v: any) => setFilterPart(medarbetareKey, 'risk', v) },
    statusSelect(medarbetareKey, medFilter.hantering ?? []),
  ]
  const brukareSelects = [
    { key: 'name', label: 'Namn', options: uniqStr((filteredBrukareData as any[]).map((r) => r.namn)), value: bruFilter.name ?? [], onChange: (v: any) => setFilterPart(brukareKey, 'name', v) },
    { key: 'personnummer', label: 'Personnummer', options: uniqStr((filteredBrukareData as any[]).map((r) => r.personnummer)), value: bruFilter.personnummer ?? [], onChange: (v: any) => setFilterPart(brukareKey, 'personnummer', v) },
    avvikelseSelect,
    { key: 'risk', label: 'Risknivå', options: riskOptions(filteredBrukareData as any[]), value: bruFilter.risk ?? [], onChange: (v: any) => setFilterPart(brukareKey, 'risk', v) },
    {
      key: 'ansvarig',
      label: 'Ansvarig handläggare',
      options: uniqStr((filteredBrukareData as any[]).map((r) => (r.ansvarig_handläggare ?? []).map((h: any) => h.namn)).flat()),
      value: bruFilter.ansvarig ?? [],
      onChange: (v: any) => setFilterPart(brukareKey, 'ansvarig', v),
    },
    statusSelect(brukareKey, bruFilter.hantering ?? []),
  ]

  // Filtrering klient-sida: avvikelse (delad) + per-flik namn/personnummer/risknivå (OR inom, AND mellan).
  const applyAvvikelse = (rows: any[]) => (graphDeviation.length > 0 ? rows.filter((r) => graphDeviation.includes(r.flaggnamn)) : rows)
  const medarbetareRows = applyAvvikelse(filteredData as any[]).filter(
    (r) =>
      (!medFilter.name?.length || medFilter.name.some((v: string) => (r.namn ?? '').includes(v))) &&
      (!medFilter.risk?.length || medFilter.risk.includes(r.nivå)) &&
      (!medFilter.hantering?.length || medFilter.hantering.includes(r.hantering?.läge ?? 'ej_hanterad'))
  )
  const brukareRows = applyAvvikelse(filteredBrukareData as any[]).filter(
    (r) =>
      (!bruFilter.name?.length || bruFilter.name.some((v: string) => (r.namn ?? '').includes(v))) &&
      (!bruFilter.personnummer?.length || (r.personnummer && bruFilter.personnummer.some((v: string) => r.personnummer.includes(v)))) &&
      (!bruFilter.risk?.length || bruFilter.risk.includes(r.nivå)) &&
      (!bruFilter.ansvarig?.length || (r.ansvarig_handläggare ?? []).some((h: any) => bruFilter.ansvarig.includes(h.namn))) &&
      (!bruFilter.hantering?.length || bruFilter.hantering.includes(r.hantering?.läge ?? 'ej_hanterad'))
  )

  const medarbetareColumns = CreateFilterConfig('medarbetare', medarbetareKey, startDate, endDate)
  const brukareColumns = CreateFilterConfig('brukare', brukareKey, startDate, endDate)

  // Dölj kolumner per flik (lokalt state, default dölj Period — som listvyerna).
  const [deselectedMedarbetare, setDeselectedMedarbetare] = useState<string[]>(['Period'])
  const [deselectedBrukare, setDeselectedBrukare] = useState<string[]>(['Period'])
  const colOptions = (cols: ColumnsType<DataType>) => cols.map((c) => c.title).filter((t): t is string => typeof t === 'string').map((t) => ({ value: t, label: t }))
  const visibleCols = (cols: ColumnsType<DataType>, deselected: string[]) => cols.filter((c) => typeof c.title !== 'string' || !deselected.includes(c.title))
  const visibleMedarbetareColumns = visibleCols(medarbetareColumns, deselectedMedarbetare)
  const visibleBrukareColumns = visibleCols(brukareColumns, deselectedBrukare)


  const getTitleString = (tab: string) => {
    switch (tab) {
      case 'tab-medarbetare':
        return 'Denna vy listar flaggor som rör en specifik utförares personal.'
      case 'tab-brukare':
        return 'Denna vy listar flaggor som rör brukare hos en specifik utförare.'
      case 'tab-trend':
        return 'Denna vy visar hur flaggor per brukare utvecklas över tid för en utförare. Den övre grafen visar vilka flaggtyper utförarens flaggor består av, den undre jämför med kommunsnittet.'
      case 'tab-riskflaggor':
        return 'Denna vy jämför en utförare mot kommunsnittet — en högre stapel indikerar ett förbättringsområde.'
      default:
        return ''
    }
  }

  const getAvgData = useCallback(
    async (f: string, t: string) => {
      const nr = ++snittFråga.current
      try {
        const data = await api.get(`/v1/utforare/${localStorage.getItem('tenant_id')}/average?from=${f}&to=${t}`, {
          headers: {
            authorization: 'Bearer ' + localStorage.getItem('hydra_access_token'),
          },
        })

        if (nr !== snittFråga.current) return
        setAvgData(data.data)
      } catch (e) {
        console.error(e)
      }
    },
    [api]
  )

  useEffect(() => {
    if (avgData && utf) {
      const filtered = graphDeviation.length > 0 ? avgData.filter((i) => graphDeviation.includes(i.name)) : avgData
      // Utförarens riskvägda värde per flaggtyp (per brukare) — för värde-sortering (störst överst).
      const valueForItem = (item: IRiskFlag) => {
        const bl = utf.brukare.filter((f: IBrukareFlag) => f.flagga === item.number).reduce((a: number, f: IBrukareFlag) => a + f.nivå, 0)
        const ml = utf.medarbetare.filter((f: IMedarbetareFlag) => f.flagga === item.number).reduce((a: number, f: IMedarbetareFlag) => a + f.nivå, 0)
        return utf.brukare_count ? (bl + ml) / utf.brukare_count : 0
      }
      const sorted = [...filtered].sort((a, b) =>
        graphSortBy === 'value' ? valueForItem(b) - valueForItem(a) : a.name.localeCompare(b.name, 'sv')
      )
      setData(generateGraphData(sorted))
    }
  }, [checked, avgData, utf, grafState])

  // Hämtar trend-historik inline, samma mönster som getAvgData ovan.
  // Hämtar ALL data per flaggtyp för intervallet — flaggtyp-filtret sker sedan klientsidan
  // (se trendChartData), så att byte av flaggtyp blir sömlöst utan refetch.
  /**
   * Vilket intervall datan som ligger inläst hör till. Nyckeln är utförare och datum.
   *
   * Hämtningen styrdes tidigare av vilken flik som var aktiv, så varje återbesök på fliken
   * hämtade allt igen – även med samma datum. Flaggöversikt hämtar när datumfönstret ändras,
   * aldrig när fliken byts, och det här gör nu likadant: datan ligger kvar i komponenten, och
   * hämtas bara om när intervallet faktiskt är ett annat.
   *
   * Hämtningen väntar ändå tills fliken öppnas första gången, så att den som aldrig tittar på
   * historiken inte betalar för den.
   */
  const trendHamtadFor = useRef<string | null>(null)

  /**
   * Sant tills mount-effekten har bytt flik. Vid färsk navigering hit (PUSH) står Redux kvar på
   * förra besökets flik under första renderingen, och mount-effekten byter till Flaggöversikt
   * först efteråt. Hade man lämnat sidan på Flagghistorik hämtades historiken annars i bakgrunden,
   * utan att fliken öppnats.
   */
  const flikenÅterställs = useRef(navigate.action !== 'POP')

  const getTrendData = useCallback(async () => {
    const nr = ++trendFråga.current
    setTrendLoading(true)
    try {
      const params = new URLSearchParams({ namn: utforare, from: trendFrom, to: trendTo })

      const res = await api.get(`/v1/utforare/${localStorage.getItem('tenant_id')}/trend?${params.toString()}`, {
        headers: {
          authorization: 'Bearer ' + localStorage.getItem('hydra_access_token'),
        },
      })

      if (nr !== trendFråga.current) return
      setTrendData(res.data?.punkter ?? [])
      setTrendFlaggnamn(res.data?.flaggnamn ?? {})
    } catch (e) {
      if (nr !== trendFråga.current) return
      console.error(e)
      setTrendData([])
      setTrendFlaggnamn({})
      // Ett misslyckat anrop ska kunna försökas igen när fliken öppnas nästa gång.
      trendHamtadFor.current = null
    } finally {
      // Laddningen gäller den senaste frågan – ett äldre svar får inte ta bort snurran.
      if (nr === trendFråga.current) setTrendLoading(false)
    }
  }, [api, utforare, trendFrom, trendTo])

  // INTE vid flaggtyp-byte – det filtreras klientsidan. OBS: beror på de stabila värdena, INTE
  // på getTrendData – useApi() skapar en ny axios-instans varje render, så callbacken byter
  // identitet varje render och skulle annars trigga en oändlig refetch-loop.
  useEffect(() => {
    if (flikenÅterställs.current || currentTab !== 'tab-trend') return
    const nyckel = `${utforare}|${trendFrom}|${trendTo}`
    if (trendHamtadFor.current === nyckel) return
    trendHamtadFor.current = nyckel
    getTrendData()
  }, [currentTab, utforare, trendFrom, trendTo])

  /**
   * Sant när Flagghistorik-fliken faktiskt syns på skärmen – en bildruta efter att den valts.
   *
   * antd tar fram fliken först i renderingen efter valet, och i renderingen däremellan ligger den
   * kvar dold. Skapades graferna då fick de storleken noll, och om Chart.js sedan ritade om dem
   * berodde på hur storleksändringen och startanimationen hann i kapp varandra. Linjegrafen blev
   * därför ibland helt tom när man bytte flik. Efter en bildruta syns fliken och graferna får
   * sin riktiga storlek från början.
   */
  const [trendFlikenSyns, setTrendFlikenSyns] = useState(false)

  useEffect(() => {
    if (currentTab !== 'tab-trend') {
      setTrendFlikenSyns(false)
      return
    }
    const raf = requestAnimationFrame(() => setTrendFlikenSyns(true))
    return () => cancelAnimationFrame(raf)
  }, [currentTab])

  useEffect(() => {
    getAvgData(startDate, endDate)
  }, [startDate, endDate])

  useEffect(() => {
    // Färsk navigering hit (PUSH, klick på utförarstapel) → landa på Flaggöversikt + ärv avvikelse från URL.
    // Tillbaka-navigering (POP, t.ex. från en flagg-detaljsida) → behåll aktuell flik + sparat filter/sortläge (#16).
    if (navigate.action !== 'POP') {
      dispatch(setUtförareTab('tab-riskflaggor'))
      flikenÅterställs.current = false
      const urlDev = splitParams(deviation).filter((d: string) => d !== 'null')
      setTableFilterAction(grafKey, { deviation: urlDev }, filterState?.[grafKey]?.sorter ?? 'alpha', dispatch)
    }
  }, [])

  useLayoutEffect(() => {
    const compute = () => {
      if (!chartWrapperRef.current) return
      const top = chartWrapperRef.current.getBoundingClientRect().top
      const available = window.innerHeight - top - BOTTOM_VIEWPORT_MARGIN
      setViewportMinHeight(Math.max(ABSOLUTE_MIN_CHART_HEIGHT, available))
    }
    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(document.documentElement)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (currentTab !== 'tab-riskflaggor') return
    const raf = requestAnimationFrame(() => {
      if (!chartWrapperRef.current) return
      const top = chartWrapperRef.current.getBoundingClientRect().top
      const available = window.innerHeight - top - BOTTOM_VIEWPORT_MARGIN
      setViewportMinHeight(Math.max(ABSOLUTE_MIN_CHART_HEIGHT, available))
    })
    return () => cancelAnimationFrame(raf)
  }, [currentTab])

  function generateGraphData(data: IRiskFlag[]) {
    const graph: IMultiChart = {
      labels: data.map((item: IRiskFlag) => item.name),
      datasets: [
        {
          label: utforare,
          data: !checked
            ? data.map((item: IRiskFlag) => {
                const brukareFlags = utf.brukare.filter((flag: IBrukareFlag) => flag.flagga === item.number).length
                const medarbetareFlags = utf.medarbetare.filter((flag: IMedarbetareFlag) => flag.flagga === item.number).length

                return (brukareFlags + medarbetareFlags) / utf.brukare_count
              })
            : data.map((item: IRiskFlag) => {
                const brukareLevel = utf.brukare.filter((flag: IBrukareFlag) => flag.flagga === item.number).reduce((accumulator: number, flag: IBrukareFlag) => accumulator + flag.nivå, 0)
                const medarbetareLevel = utf.medarbetare
                  .filter((flag: IMedarbetareFlag) => flag.flagga === item.number)
                  .reduce((accumulator: number, flag: IMedarbetareFlag) => accumulator + flag.nivå, 0)

                return (brukareLevel + medarbetareLevel) / utf.brukare_count
              }),
          borderColor: '#1E73A8FF',
          backgroundColor: '#1E73A833',
        },
        {
          label: 'Kommunsnitt',
          data: !checked ? data.map((item: IRiskFlag) => Number(item.avg)) : data.map((item: IRiskFlag) => Number(item.level_avg)),
          borderColor: '#188F50FF',
          backgroundColor: '#188F5033',
        },
      ],
    }
    return graph
  }

  const chartHeight = Math.max(viewportMinHeight, (avgData?.length ?? 0) * PX_PER_BAR)

  // Riskvägt flaggor/brukare per månad, filtrerat på det delade flaggfiltret — helt klientsidan.
  // Inget filter = alla flaggtyper. Byte av flagga räknar bara om detta, ingen refetch.
  const trendChartData = useMemo(() => {
    const points = trendData ?? []
    const valueFor = (byType: { [type: string]: number }, denom: number) => {
      if (!denom) return 0
      const types = trendValdaTyper ?? Object.keys(byType)
      return types.reduce((acc, t) => acc + (byType[t] ?? 0), 0) / denom
    }
    /*
     * Linjen i kanttonen (alfa FF) på BORDER_WIDTH, prickarna helt ifyllda i samma ton.
     *
     * Det som gjorde linjen skarp från början var tjockleken, inte färgen: den stod på Chart.js
     * standard, 3 px. En linje i fyllningstonen (alfa 33) blev för svag att följa. Kanttonen på
     * halva tjockleken är tydlig utan tyngd.
     *
     * Prickarna har ingen ljus mitt. Staplarnas två toner – ljus fyllning, mörk kant – fungerar på
     * ytor, men på en prick med 3 px radie blev den ljusa mitten ett hål i linjen, och linjen såg
     * ut att ta slut där den gick in i pricken. En heldragen prick har mer tyngd än den tunna
     * linjen och står ut som mätvärde av sig själv. Prickarna är mätvärdena, en per körningsmånad;
     * linjen mellan dem är ingen mätning.
     *
     * Blått och grönt, inte benchmarkings gråblå linjepalett: utföraren är blå och kommunsnittet
     * grönt även i Flaggöversikt på samma sida, och färgerna ska betyda samma sak i båda flikarna.
     */
    const serie = (farg: typeof BLUE | typeof GREEN) => ({
      borderColor: farg.border,
      borderWidth: BORDER_WIDTH,
      backgroundColor: farg.fill,
      pointBackgroundColor: farg.border,
      pointBorderColor: farg.border,
      pointHoverBackgroundColor: farg.border,
      pointHoverBorderColor: farg.border,
      tension: 0.2,
    })
    return {
      labels: points.map((p) => moment(p.month, 'YYYY-MM').format('MMM-YY')),
      datasets: [
        {
          label: utforare,
          data: points.map((p) => valueFor(p.utforareByType, p.brukareCount)),
          ...serie(BLUE),
        },
        {
          label: 'Kommunsnitt',
          data: points.map((p) => valueFor(p.kommunByType, p.kommunBrukareCount)),
          ...serie(GREEN),
        },
      ],
    }
  }, [trendData, trendValdaTyper, utforare])

  const trendOptions = {
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    // Ingen reserverad plats till höger. Förklaringen ligger under rubriken i stället, så att
    // båda graferna får samma bredd och x-axlarna står rakt under varandra.
    layout: { padding: { top: 8 } },
    scales: {
      y: {
        beginAtZero: true,
        afterFit: trendYAxelBredd,
        // Beskrivande text → 14px apple-system, som kategori-etiketterna i Utförare/Flaggöversikt.
        title: { display: true, text: 'Antal flaggor per brukare', color: CHART_TICK_COLOR, font: { family: CHART_FONT_FAMILY, size: 14, weight: 'normal' as const } },
        // Siffrorna i samma format som månaderna och rubriken, som värdeaxeln i Utförare/Flaggöversikt.
        ticks: { color: CHART_TICK_COLOR, font: { family: CHART_FONT_FAMILY, size: 14, weight: 'normal' as const } },
      },
      x: {
        grid: { display: false },
        ticks: { color: CHART_TICK_COLOR, font: { family: CHART_FONT_FAMILY, size: 14, weight: 'normal' as const } },
      },
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        ...grafTooltip(true),
        callbacks: {
          labelColor: förklaringsfärg,
          label: (item: any) => `${item.dataset?.label ?? ''}: ${perBrukare(item.parsed.y)}`,
        },
      },
    },
  }

  /**
   * Staplarna: samma riskvägda tal som linjen, men inte hopslagna – ett segment per flaggtyp.
   * Varje stapels totalhöjd blir därför exakt utförarens linje i jämförelsegrafen.
   *
   * Linjen svarar på om utföraren är bättre eller sämre än kommunen. Staplarna svarar på vad
   * det består av. En total som ligger still kan dölja att en flaggtyp gått ned medan en
   * annan gått upp, och det är den förändringen staplarna gör synlig.
   *
   * Färgen följer flaggtypens plats bland ALLA typer i intervallet, inte bland de filtrerade,
   * så att en flaggtyp inte byter färg när filtret ändras. Samma palett som volymmodulen.
   */
  const trendStapelData = useMemo(() => {
    const points = trendData ?? []
    const synliga = trendValdaTyper ? trendTyper.filter((t) => trendValdaTyper.includes(t)) : trendTyper
    return {
      labels: points.map((p) => moment(p.month, 'YYYY-MM').format('MMM-YY')),
      datasets: synliga.map((t) => {
        const farg = getColorByIndex(trendTyper.indexOf(t))
        return {
          label: trendFlaggnamn[t] ?? `Flaggtyp ${t}`,
          data: points.map((p) => (p.brukareCount ? (p.utforareByType[t] ?? 0) / p.brukareCount : 0)),
          backgroundColor: farg.fill,
          borderColor: farg.border,
          // Benchmarkings kanttjocklek, inte en egen. Stod tidigare på 1.
          borderWidth: BORDER_WIDTH,
        }
      }),
    }
  }, [trendData, trendValdaTyper, trendTyper, trendFlaggnamn])

  // Samma flaggtyper, namn och färger som staplarna – men i omvänd ordning. Chart.js lägger första
  // flaggtypen längst ned i stapeln; listad uppifrån i samma ordning blev förklaringen upp och ned
  // mot staplarna. Vänd står översta raden för översta segmentet.
  const trendStapelForklaring: TrendForklaringPost[] = trendStapelData.datasets
    .map((d) => ({ text: d.label, fyllning: d.backgroundColor, kant: d.borderColor }))
    .reverse()

  const trendStapelOptions = {
    maintainAspectRatio: false,
    // Samma luft upptill som linjegrafen, så att plottytorna börjar på samma höjd i sina rutor.
    layout: { padding: { top: 8 } },
    // Närmaste segment, inte hela månaden: med många flaggtyper skulle en tooltip över alla bli
    // en lång lista. Den som pekar på ett segment vill veta vilken flagga det är.
    interaction: { mode: 'nearest' as const, intersect: false },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: CHART_TICK_COLOR, font: { family: CHART_FONT_FAMILY, size: 14, weight: 'normal' as const } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        afterFit: trendYAxelBredd,
        title: { display: true, text: 'Antal flaggor per brukare', color: CHART_TICK_COLOR, font: { family: CHART_FONT_FAMILY, size: 14, weight: 'normal' as const } },
        ticks: { color: CHART_TICK_COLOR, font: { family: CHART_FONT_FAMILY, size: 14, weight: 'normal' as const } },
      },
    },
    plugins: {
      // Ingen förklaring under grafen. Med många flaggtyper tar den mer plats än den ger;
      // tooltipen och filtret namnger flaggtyperna.
      legend: { display: false },
      title: { display: false },
      tooltip: {
        ...grafTooltip(true),
        callbacks: {
          // Månaden som rubrik, flaggan och värdet under med färgrutan framför. Månaden behövs här:
          // vid långa intervall hoppar x-axeln över etiketter, och då står den inte under stapeln.
          title: (items: any[]) => items[0]?.label ?? '',
          labelColor: förklaringsfärg,
          label: (item: any) => `${item.dataset?.label ?? ''}: ${perBrukare(item.parsed.y)}`,
        },
      },
    },
  }

  function handleTrendDatesChange(range: any) {
    if (!range || !range[0] || !range[1]) return
    setTrendFrom(range[0].startOf('month').format('YYYY-MM-DD'))
    setTrendTo(range[1].endOf('month').format('YYYY-MM-DD'))
  }

  const initieraAction = (
    <Tooltip overlayClassName="flag-tooltip" title={btnDisabled ? <>Välj en eller flera rader<br />i tabellen nedan</> : ''}>
      <span style={btnDisabled ? { cursor: 'not-allowed' } : undefined}>
        <Button type="primary" onClick={() => setHandleOpen(true)} disabled={btnDisabled} style={{ width: 165, padding: '0 8px', ...(btnDisabled ? { pointerEvents: 'none' } : {}) }}>
          Hantera flagga{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
        </Button>
      </span>
    </Tooltip>
  )

  return (
    <>
      <ViewHeader heading={utforare} description={getTitleString(currentTab)} />
      <Tabs defaultActiveKey={'tab-riskflaggor'} activeKey={currentTab} onTabClick={(key: string) => dispatch(setUtförareTab(key))} style={{ marginTop: -12 }}>
        <Tabs.TabPane tab={'Flaggöversikt'} key={'tab-riskflaggor'}>
          <FilterToolbar
            startDate={startDate}
            endDate={endDate}
            onDatesChange={handleDatesChange}
            onOpenChange={() => undefined}
            filterSelects={[avvikelseSelect]}
            onResetFilters={() => setGraphDeviation([])}
            sortOptions={[
              { value: 'alpha', label: 'Alfabetisk' },
              { value: 'value', label: 'Antal flaggor per brukare' },
            ]}
            sortValue={graphSortBy}
            onSortChange={(v) => setGraphSortBy(v as 'alpha' | 'value')}
          />
          <div style={{ marginTop: 8 }}>
            <h1 className="text-center" style={{ fontSize: 20, marginBottom: 6 }}>
              Antal flaggor per brukare, jämförelse med kommunsnitt
            </h1>
            <div ref={chartWrapperRef} style={{ position: 'relative', height: chartHeight }}>
              {data.datasets.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', height: '100%', paddingTop: 80 }}>
                  <Spin />
                </div>
              ) : (
                <>
                  <Bar options={generateOptions()} data={data} plugins={[färgrutaIHöjdMedTexten]} />
                  <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', flexDirection: 'column', gap: 6, pointerEvents: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 14, height: 14, backgroundColor: '#1E73A833', border: '1.5px solid #1E73A8FF', borderRadius: 2, flexShrink: 0 }} />
                      <span style={{ fontSize: 14, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{utforare}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 14, height: 14, backgroundColor: '#188F5033', border: '1.5px solid #188F50FF', borderRadius: 2, flexShrink: 0 }} />
                      <span style={{ fontSize: 14 }}>Kommunsnitt</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab={'Flagghistorik'} key={'tab-trend'}>
          {/*
           * Samma verktygsrad som de andra flikarna. Kontrollerna låg tidigare i flikradens
           * högerkant (tabBarExtraContent), men den platsen finns inte längre – varje flik
           * bär sin egen FilterToolbar.
           *
           * Egna datum, inte översiktens startDate/endDate: historiken har sitt eget intervall.
           * Datum och flaggtyp gäller båda graferna. Byte av flaggtyp filtreras klientsidan,
           * utan ny hämtning.
           */}
          <FilterToolbar
            startDate={trendFrom}
            endDate={trendTo}
            onDatesChange={handleTrendDatesChange}
            onOpenChange={() => undefined}
            filterSelects={[trendAvvikelseSelect]}
            onResetFilters={() => setGraphDeviation([])}
          />
          <div style={{ marginTop: 8 }}>
            {/* Graferna skapas först när fliken syns, se trendFlikenSyns. */}
            {!trendFlikenSyns ? null : trendLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: TREND_GRAF_HOJD, minHeight: TREND_GRAF_MINHOJD }}>
                <Spin />
              </div>
            ) : !trendData || trendData.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: TREND_GRAF_HOJD, minHeight: TREND_GRAF_MINHOJD }}>
                <span style={{ color: 'rgba(0,0,0,0.45)' }}>Ingen flagghistorik för vald period.</span>
              </div>
            ) : (
              // Staplarna överst, linjen under: först vad utförarens flaggor består av, sedan hur
              // utföraren står sig mot kommunen.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <section>
                  <h1 className="text-center" style={{ fontSize: 20, marginBottom: 6 }}>
                    Antal flaggor per brukare över tid, per flaggtyp
                  </h1>
                  {/* Grafen och förklaringen på en rad. minWidth: 0 låter grafen krympa – utan det
                      växer canvasen ut över förklaringskolumnen. */}
                  <div style={{ display: 'flex', height: TREND_GRAF_HOJD, minHeight: TREND_GRAF_MINHOJD }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                      {/* Linjen visar en nollinje för en utförare utan flaggor och säger ändå något.
                          Staplarna blir en tom yta, så där behövs en mening. */}
                      {trendTyper.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <span style={{ color: 'rgba(0,0,0,0.45)' }}>Utföraren har inga flaggor under vald period.</span>
                        </div>
                      ) : (
                        <Bar options={trendStapelOptions} data={trendStapelData} plugins={[färgrutaIHöjdMedTexten]} />
                      )}
                    </div>
                    {/* Kolumnen står kvar även när grafen är tom, så att sidan inte hoppar i sidled
                        när ett nytt intervall ger eller tar bort flaggor. */}
                    <TrendForklaring poster={trendStapelForklaring} />
                  </div>
                </section>

                <section>
                  <h1 className="text-center" style={{ fontSize: 20, marginBottom: 6 }}>
                    Antal flaggor per brukare över tid, jämförelse med kommunsnitt
                  </h1>
                  <div style={{ display: 'flex', height: TREND_GRAF_HOJD, minHeight: TREND_GRAF_MINHOJD }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                      <Line options={trendOptions} data={trendChartData} plugins={[färgrutaIHöjdMedTexten]} />
                    </div>
                    <TrendForklaring
                      poster={[
                        { text: utforare, fyllning: BLUE.fill, kant: BLUE.border },
                        { text: 'Kommunsnitt', fyllning: GREEN.fill, kant: GREEN.border },
                      ]}
                    />
                  </div>
                </section>
              </div>
            )}
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab={'Personal'} key={'tab-medarbetare'}>
          <FilterToolbar
            startDate={startDate}
            endDate={endDate}
            onDatesChange={handleDatesChange}
            onOpenChange={() => undefined}
            filterSelects={medarbetareSelects}
            onResetFilters={() => clearFilter(medarbetareKey)}
            columnOptions={colOptions(medarbetareColumns)}
            deselectedColumns={deselectedMedarbetare}
            onDeselectedColumnsChange={setDeselectedMedarbetare}
            total={`${medarbetareRows.length} av ${(filteredData as any[]).length}`}
            action={initieraAction}
          />
          <Table
            className="flag-table"
            columns={visibleMedarbetareColumns}
            rowSelection={{ ...rowSelection }}
            scroll={{ x: sumColumnWidths(visibleMedarbetareColumns), y: 'calc(100vh - 290px)' }}
            dataSource={medarbetareRows}
            pagination={false}
            bordered
            size="small"
            onChange={(_pagination, _filters, sorter) => setSorterFor(medarbetareKey, sorter)}
            // loading={loadingState}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab={'Brukare'} key={'tab-brukare'}>
          <FilterToolbar
            startDate={startDate}
            endDate={endDate}
            onDatesChange={handleDatesChange}
            onOpenChange={() => undefined}
            filterSelects={brukareSelects}
            onResetFilters={() => clearFilter(brukareKey)}
            columnOptions={colOptions(brukareColumns)}
            deselectedColumns={deselectedBrukare}
            onDeselectedColumnsChange={setDeselectedBrukare}
            total={`${brukareRows.length} av ${(filteredBrukareData as any[]).length}`}
            action={initieraAction}
          />
          <Table
            className="flag-table"
            columns={visibleBrukareColumns}
            rowSelection={{ ...rowSelection }}
            scroll={{ x: sumColumnWidths(visibleBrukareColumns), y: 'calc(100vh - 290px)' }}
            dataSource={brukareRows}
            pagination={false}
            size="small"
            bordered
            onChange={(_pagination, _filters, sorter) => setSorterFor(brukareKey, sorter)}
            // loading={loadingState}
            // onRow={(record, rowIndex) => {
            //   return {
            //     onClick: (event: any) => {
            //       event.target.id !== 'flagLink' &&
            //         navigate.push('/app/brukare/id=' + record.personnummer + '&namn=' + record.namn)
            //     },
            //   }
            // }}
          />
        </Tabs.TabPane>
      </Tabs>
      <HandleFlagModal open={handleOpen} rows={selectedRows} onClose={() => setHandleOpen(false)} onDone={efterHantering} />
    </>
  )
}

export default SingleUtforare
